import { getTrackListComments } from "@api/comments";
import type { CommentRating, TracklistComment } from "@api/comments/types";
import type { Tracklist } from "@api/tracklists/types";
import AddComment from "@components/AddComment";
import Button from "@components/Button";
import ErrorFallback from "@components/ErrorFallback";
import FadedText from "@components/FadedText";
import InfiniteFeed from "@components/InfiniteFeed";
import { AltText, Column, Row } from "@components/Layout";
import SortIcon from "@components/SortIcon";
import useI18n from "@contexts/I18nProvider/hooks/useI18n";
import PlayButton from "@layouts/App/context/Player/components/PlayButton";
import { sidebarElementContext } from "@layouts/App/context/SidebarElement";
import {
	type ComponentProps,
	ErrorBoundary,
	Show,
	type VoidComponent,
	createSignal,
	onMount,
	useContext,
} from "solid-js";
import { createStore, unwrap } from "solid-js/store";
import { Portal } from "solid-js/web";
import CommentRow from "./components/CommentRow";
import CommentRowTemplate from "./components/CommentRow/index.template";
import usePlayComments from "./hooks/usePlayComments";
import { AddCommentRow, CommentsWrapperCard, HeaderRow } from "./styled";

const LIMIT = 10;

const Inner: VoidComponent<{ uuid: Tracklist["uuid"] }> = (props) => {
	const { t } = useI18n();
	const [comments, setComments] = createStore<TracklistComment[]>([]);
	const [page, setPage] = createSignal(1);
	const [canLoadMore, setCanLoadMore] = createSignal(true);
	const [loading, setLoading] = createSignal(false);
	const [reversed, setReversed] = createSignal(false);
	const playComments = usePlayComments();

	const handleLoadTracklist = () => {
		if (!canLoadMore() || loading()) return;
		setLoading(true);
		getTrackListComments(props.uuid, page())
			.then((res) => {
				setComments((s) => [...s, ...res]);
				if (res.length !== LIMIT) throw new Error("End of data");
				setPage((s) => s + 1);
			})
			.catch(() => setCanLoadMore(false))
			.finally(() => setLoading(false));
	};
	onMount(handleLoadTracklist);

	const handleRating = (index: number, rating: CommentRating) => {
		setComments(index, "rating", "user_choice", rating);
	};

	const handleAddComment = (p = 1) => {
		getTrackListComments(props.uuid, p).then((res) => {
			const filtered = res.filter((c) => !comments.find((i) => i.uuid === c.uuid));
			setComments((s) => [...(reversed() ? [] : filtered), ...s, ...(reversed() ? filtered : [])]);
			if (filtered.length === res.length && res.length === LIMIT) handleAddComment(p + 1);
		});
	};
	const handleDeleteComment = (uuid: TracklistComment["uuid"]) => setComments((s) => s.filter((i) => i.uuid !== uuid));

	const reverseComments = () => {
		const c = [...unwrap(comments)];
		c.reverse();
		setComments(c);
		setReversed((s) => !s);
	};

	return (
		<>
			<HeaderRow justifyContent="space-between" alignItems="center" padding="20px">
				<Row w="max-content" gap="5px" alignItems="center">
					<h2>{t("app.programme.comments.header")}</h2>
					<AltText>({comments.length})</AltText>
				</Row>
				<Show when={comments.length}>
					<Column w="max-content" gap="5px" alignItems="center">
						<PlayButton
							diameter="35px"
							onClick={() => playComments(comments)}
							tracklistUuid={`comments-${props.uuid}`}
						/>
						<FadedText fontSize="12px">{t("global.playAll")}</FadedText>
					</Column>
				</Show>
			</HeaderRow>
			<InfiniteFeed
				count={comments.length}
				loading={loading()}
				estimateSize={() => 250}
				onInterSection={handleLoadTracklist}
			>
				{(item) => (
					<Show when={comments[item.index]} fallback={<CommentRowTemplate />}>
						<CommentRow
							comment={comments[item.index]}
							even={item.index % 2 === 0}
							index={item.index}
							onRating={(r) => handleRating(item.index, r)}
							onDelete={() => handleDeleteComment(comments[item.index].uuid)}
							reversed={reversed()}
							total={comments.length}
							onPlay={() => playComments([comments[item.index]])}
						/>
					</Show>
				)}
			</InfiniteFeed>
			<AddCommentRow padding="20px" justifyContent="space-between" gap="5px">
				<AddComment uuid={props.uuid} onAdd={handleAddComment}>
					<Button h="40px" w="100%">
						{t("app.programme.comments.add")}
					</Button>
				</AddComment>
				<Button h="40px" borderRadius="5px" padding="0 10px" onclick={reverseComments}>
					<SortIcon />
				</Button>
			</AddCommentRow>
		</>
	);
};

const Comments: VoidComponent<ComponentProps<typeof Inner>> = (props) => {
	const { element } = useContext(sidebarElementContext);
	return (
		<Show when={element()} keyed>
			{(el) => (
				<Portal mount={el}>
					<Column h="100%" padding="0 20px 20px">
						<CommentsWrapperCard h="100%">
							<ErrorBoundary fallback={<ErrorFallback />}>
								<Inner {...props} />
							</ErrorBoundary>
						</CommentsWrapperCard>
					</Column>
				</Portal>
			)}
		</Show>
	);
};
export default Comments;
